// Hooks de Administración · {Tiendas y zonas} (plan 2026-10-30 §2.4).
// Claves propias bajo ["administracion", "activos", …] —mismo precedente
// que features/admin/queries.ts— para que invalidar esta sección no
// barra la caché de las pantallas de operación, y al revés.
//
// Las mutaciones sí invalidan las claves globales que leen las mismas
// filas: el tablero facility (/tiendas, Overview), la lista de tiendas
// asignables del formulario de invitación, la cartera por grupo y la
// resolución de nombres de alcance. Lo que no se invalida no se ve.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type {
  AssetCreateBody,
  AssetGroupCreateBody,
  AssetGroupUpdateBody,
  AssetUpdateBody,
} from "@bitacorapro/api-types";
import { queryKeys } from "../../../lib/api/queries";
import { adminAssetGroupsApi, adminAssetsApi } from "./api";

export const activosQueryKeys = {
  assets: ["administracion", "activos", "assets"] as const,
  groups: ["administracion", "activos", "groups"] as const,
} as const;

export function useAdminAssets() {
  return useQuery({
    queryKey: activosQueryKeys.assets,
    queryFn: () => adminAssetsApi.listAll(),
    staleTime: 60 * 1000,
  });
}

export function useAdminAssetGroups() {
  return useQuery({
    queryKey: activosQueryKeys.groups,
    queryFn: () => adminAssetGroupsApi.list(),
    staleTime: 60 * 1000,
  });
}

// Una tienda cambió (nombre, zona, dirección, jefe) o nació: además de
// las dos listas de esta sección, se mueven el tablero facility, la lista
// de asignables, la cartera y «Comparar tiendas» (lleva el nombre de cada
// tienda con un minuto de staleTime). `["assets", "resolve"]` y
// `["assets", "compare"]` son los prefijos de queryKeys.resolveScopes y
// queryKeys.compareStores (funciones), de ahí los literales.
function invalidateAfterAssetWrite(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: activosQueryKeys.assets });
  void qc.invalidateQueries({ queryKey: activosQueryKeys.groups });
  void qc.invalidateQueries({ queryKey: queryKeys.facilityDashboard });
  void qc.invalidateQueries({ queryKey: queryKeys.assignableAssets });
  void qc.invalidateQueries({ queryKey: queryKeys.carteraDashboardAll });
  void qc.invalidateQueries({ queryKey: ["assets", "resolve"] });
  void qc.invalidateQueries({ queryKey: ["assets", "compare"] });
}

// Una zona cambió de nombre o de responsable, o nació: las tiendas no se
// mueven, pero el tablero, la cartera y la comparación muestran el nombre
// de la zona y la lista de asignables lo lleva por fila.
function invalidateAfterGroupWrite(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: activosQueryKeys.groups });
  void qc.invalidateQueries({ queryKey: queryKeys.facilityDashboard });
  void qc.invalidateQueries({ queryKey: queryKeys.assignableAssets });
  void qc.invalidateQueries({ queryKey: queryKeys.carteraDashboardAll });
  void qc.invalidateQueries({ queryKey: ["assets", "resolve"] });
  void qc.invalidateQueries({ queryKey: ["assets", "compare"] });
}

export function useCreateAdminAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssetCreateBody) => adminAssetsApi.create(body),
    onSuccess: () => invalidateAfterAssetWrite(qc),
  });
}

export function useUpdateAdminAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: AssetUpdateBody }) =>
      adminAssetsApi.update(vars.id, vars.body),
    onSuccess: () => invalidateAfterAssetWrite(qc),
  });
}

export function useCreateAdminAssetGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssetGroupCreateBody) => adminAssetGroupsApi.create(body),
    onSuccess: () => invalidateAfterGroupWrite(qc),
  });
}

export function useUpdateAdminAssetGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: AssetGroupUpdateBody }) =>
      adminAssetGroupsApi.update(vars.id, vars.body),
    onSuccess: () => invalidateAfterGroupWrite(qc),
  });
}
